import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../auth/contexts/AuthenticationContext';
import { classifyPendingActionError } from '../../shared/api/errors';
import { createPendingAction } from '../../shared/storage/pendingAction';

import { partnerApi, PENDING_PARTNER_SLUG_KEY, PartnerClaimResponse } from './endpoints';

/** Shared with the invite-link and self-enroll claims — see pendingAction. */
export const pendingPartnerSlug = createPendingAction(PENDING_PARTNER_SLUG_KEY);

export function useClaimPartnerSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => partnerApi.claim(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

// Mounted globally by InviteClaimBanner, like the other two pending claims. It
// used to run only on DashboardPage, so a partner claimant landing on
// /admin/dashboard, a stored returnPath, or /welcome/:slug never claimed at all.
export function usePendingPartnerClaim(
  onClaimed?: (result: PartnerClaimResponse) => void,
  onError?: (message: string) => void,
) {
  const { isAuthenticated, isLoading } = useAuth();
  const { mutate } = useClaimPartnerSeat();
  const firedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || firedRef.current) return;
    const slug = pendingPartnerSlug.read();
    if (!slug) return;

    firedRef.current = true;
    mutate(slug, {
      onSuccess: (result) => {
        pendingPartnerSlug.clear();
        onClaimed?.(result);
      },
      onError: (err) => {
        const { definitive, message } = classifyPendingActionError(err, {
          definitiveFallback: 'We could not add your course automatically. Please contact your administrator.',
          unreachable: "We couldn't reach the Human Risk Academy to claim your seat. Check your connection and refresh this page.",
        });
        if (definitive) pendingPartnerSlug.clear();
        else firedRef.current = false;
        // Previously the transient branch reported nothing at all, so a network
        // failure left the claimant on an empty dashboard with no explanation.
        onError?.(message);
      },
    });
  }, [isAuthenticated, isLoading, mutate, onClaimed, onError]);
}
